import React, { useState, FC, useRef, useEffect, useMemo, useContext } from "react";
import { Input, InputNumber, AutoComplete } from "antd";
import { GlobalContext } from "~/context/global-context";

type Props = {
  onChange?: (newValue: number) => void;
  defaultValue?: number;
  minMinutes?: number | null;
  maxMinutes?: number | null;
  decimalPoints?: number;
  disabled?: boolean;
};

const minutesAsHoursAndMinutes = (minutes?: number | null) => {
  return minutes === null || minutes === undefined
    ? undefined
    : {
        hours: Math.floor(minutes / 60),
        minutes: minutes % 60,
      };
};

const minutesAsDecimalHours = (minutes?: number | null) => {
  return minutes === null || minutes === undefined
    ? undefined
    : { hours: minutes / 60, minutes: 0 };
};

function roundDownToNearestDecimalPlaces(value: number, decimalPlaces: number): number {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(value * factor) / factor;
}

const parser = (value: string) => value.replace(/\s?h/g, "");
// const formatter = (value: string) => value && `${value.toString().replace(/\s?h/g, "")} h`;

export const TimeDurationInput: FC<Props> = ({
  defaultValue,
  onChange,
  minMinutes: min,
  maxMinutes: max,
  decimalPoints = 1,
  disabled,
}) => {
  const { decimalTimeInput: decimal } = useContext(GlobalContext);

  const timeDurationInputRef = useRef<any>(null);

  const [[hours, minutes], setDuration] = useState<[number?, number?]>([
    defaultValue && Math.floor(defaultValue / 60),
    defaultValue && Math.floor(defaultValue % 60),
  ]);

  const calculatedValue = useMemo(() => {
    return (hours || 0) * 60 + (minutes || 0);
  }, [hours, minutes]);

  const minTime = decimal ? minutesAsDecimalHours(min) : minutesAsHoursAndMinutes(min);

  const maxTime = decimal ? minutesAsDecimalHours(max) : minutesAsHoursAndMinutes(max);

  useEffect(() => {
    if (calculatedValue !== defaultValue) {
      onChange?.(calculatedValue);
    }
  }, [calculatedValue]);

  useEffect(() => {
    setDuration([
      defaultValue && Math.floor(defaultValue / 60),
      defaultValue && Math.floor(defaultValue % 60),
    ]);
  }, [defaultValue]);

  if (decimal) {
    return (
      <InputNumber
        ref={timeDurationInputRef}
        onFocus={() => {
          if (timeDurationInputRef.current) {
            timeDurationInputRef.current.select();
          }
        }}
        size="small"
        min={minTime?.hours || 0}
        max={maxTime?.hours || 100000}
        placeholder="hours"
        value={Number((calculatedValue / 60).toFixed(1))}
        step={0.1}
        onChange={(value) => {
          const numericValue = value
            ? Number(
                parser(
                  `${decimalPoints ? roundDownToNearestDecimalPlaces(value, decimalPoints) : value}`
                )
              )
            : 0;
          const totalMinutes = numericValue ? numericValue * 60 : 0;
          setDuration([Math.floor(totalMinutes / 60), Math.floor(totalMinutes % 60)]);
        }}
        parser={parser as any}
        formatter={(value, info) =>
          value && !info.userTyping
            ? decimalPoints
              ? `${roundDownToNearestDecimalPlaces(value, decimalPoints)} h`
              : `${value.toString().replace(/\s?h/g, "")} h `
            : `${value}`
        }
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            timeDurationInputRef.current?.blur();
            return false;
          }
          if ((e.key < "0" || e.key > "9") && e.key !== "." && e.key !== "Backspace") {
            e.preventDefault();
            return false;
          }
        }}
        disabled={disabled}
      />
    );
  } else {
    return (
      <>
        <InputNumber
          ref={timeDurationInputRef}
          onFocus={() => {
            if (timeDurationInputRef.current) {
              timeDurationInputRef.current.select();
            }
          }}
          size="small"
          min={minTime?.hours || 0}
          max={maxTime?.hours || 100000}
          placeholder="hours"
          value={hours || 0}
          onChange={(value) => {
            setDuration([parseInt((value as any) || 0), minutes]);
          }}
          parser={parser as any}
          formatter={(value, info) =>
            value && !info.userTyping ? `${value.toString().replace(/\s?h/g, "")} h ` : `${value}`
          }
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              timeDurationInputRef.current?.blur();
              return false;
            }
            if ((e.key < "0" || e.key > "9") && e.key !== "Backspace") {
              e.preventDefault();
              return false;
            }
          }}
          disabled={disabled}
        />
        <AutoComplete
          options={[{ value: 0 }, { value: 15 }, { value: 30 }, { value: 45 }]}
          onChange={(value) => {
            setDuration([hours, parseInt(value) || undefined]);
          }}
          value={minutes?.toString() || "0"}
          style={{ width: "80px" }}
          disabled={disabled}
        >
          <Input
            size="small"
            min={minTime?.minutes || 0}
            max={maxTime?.minutes || 60}
            placeholder="min"
            step={6}
            onKeyPress={(e: React.KeyboardEvent) => {
              if (e.key < "0" || e.key > "9") {
                e.preventDefault();
                return false;
              }
            }}
            suffix="min"
          />
        </AutoComplete>
      </>
    );
  }
};
